import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Upload, Users, Building2, LayoutTemplate, LogOut,
  ChevronLeft, ChevronRight, X, Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ConcordiaLogo } from './UI';

export default function Sidebar({ isOpen, collapsed, onToggleCollapse, onClose }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const items = [
    { path: '/', label: 'Contratos', icon: LayoutDashboard },
    ...(isAdmin ? [
      { path: '/admin/upload', label: 'Cargar Datos', icon: Upload },
      { path: '/admin/users', label: 'Usuarios', icon: Users },
      { path: '/admin/orgs', label: 'Organizaciones', icon: Building2 },
      { path: '/admin/dashboards', label: 'Gestionar Dashboards', icon: LayoutTemplate },
    ] : []),
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname.startsWith('/contracts');
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => { navigate(path); onClose(); };

  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    document.body.style.cursor = 'wait';
    try {
      await logout();
      navigate('/login');
    } finally {
      document.body.style.cursor = '';
      setLoggingOut(false);
    }
  };

  return (
    <div
      className={`sidebar${collapsed ? ' collapsed' : ''}${isOpen ? ' mobile-open' : ''}`}
      style={{
        minHeight: '100vh',
        background: 'var(--surface-card)',
        borderRight: '1px solid var(--border-default)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 10,
        overflowX: 'hidden',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* Header — Logo */}
      <div style={{
        padding: collapsed ? '14px 0 12px' : '14px 12px 12px',
        borderBottom: '1px solid var(--border-default)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: 6,
        minHeight: 64,
      }}>
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minWidth: 0, flex: 1 }}
          onClick={() => handleNav('/')}
        >
          {collapsed
            ? <ConcordiaLogo variant="icon" />
            : <ConcordiaLogo variant="sidebar" />
          }
        </div>

        {/* Mobile close */}
        <button
          className="sidebar-close-btn"
          onClick={onClose}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--brand-muted)', padding: 4, flexShrink: 0,
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X size={18} />
        </button>

        {/* Desktop collapse/expand toggle */}
        <button
          onClick={onToggleCollapse}
          title={collapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}
          className="sidebar-desktop-toggle"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--brand-muted)', padding: 4, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav style={{
        flex: 1,
        padding: collapsed ? '12px 0' : '12px 8px',
        display: 'flex', flexDirection: 'column', gap: 2,
      }}>
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              title={collapsed ? item.label : undefined}
              style={{
                display: 'flex', alignItems: 'center',
                gap: collapsed ? 0 : 10,
                padding: collapsed ? '11px 0' : '10px 12px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: collapsed ? 0 : 8,
                border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: active ? 600 : 400,
                textAlign: 'left', width: '100%',
                background: active ? 'var(--surface-subtle)' : 'transparent',
                color: active ? 'var(--brand-primary)' : 'var(--brand-muted)',
                borderLeft: active && !collapsed ? '3px solid var(--brand-primary)' : '3px solid transparent',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.background = 'var(--surface-subtle)';
                  e.currentTarget.style.color = 'var(--brand-ink)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--brand-muted)';
                }
              }}
            >
              <Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Footer — User info */}
      <div style={{
        padding: collapsed ? '10px 0' : '12px 14px',
        borderTop: '1px solid var(--border-default)',
        display: 'flex', flexDirection: 'column',
        alignItems: collapsed ? 'center' : 'stretch', gap: 8,
      }}>
        {collapsed ? (
          <>
            <div
              title={`${user?.nombre} · ${user?.organization?.name}`}
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'default',
              }}
            >
              {user?.nombre?.charAt(0) || '?'}
            </div>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              title={loggingOut ? 'Cerrando sesión...' : 'Cerrar sesión'}
              style={{
                background: 'none', border: 'none', cursor: loggingOut ? 'wait' : 'pointer',
                color: 'var(--brand-muted)', padding: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {loggingOut
                ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} />
                : <LogOut size={15} />}
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, color: '#fff',
              }}>
                {user?.nombre?.charAt(0) || '?'}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <p style={{
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--brand-ink)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {user?.nombre}
                </p>
                <p style={{
                  fontSize: 10, color: 'var(--brand-muted)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {user?.organization?.name}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 10px', borderRadius: 6,
                border: '1px solid var(--border-default)', background: 'transparent',
                color: 'var(--brand-muted)', fontSize: 12,
                cursor: loggingOut ? 'wait' : 'pointer', width: '100%',
                opacity: loggingOut ? 0.7 : 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (loggingOut) return;
                e.currentTarget.style.borderColor = 'var(--state-danger)';
                e.currentTarget.style.color = 'var(--state-danger)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.color = 'var(--brand-muted)';
              }}
            >
              {loggingOut
                ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Cerrando sesión...</>
                : <><LogOut size={13} /> Cerrar sesión</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
